import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { ThemeId, applyTheme, loadStoredTheme, resolveTheme } from "../themes";

interface ThemeContextValue {
  /** The user's chosen theme setting (may be "system"). */
  theme: ThemeId;
  /** The resolved colour scheme after applying OS pref for "system". */
  resolvedScheme: "light" | "dark";
  /** Update the active theme (applies + persists to localStorage). */
  setTheme: (id: ThemeId) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: "light",
  resolvedScheme: "light",
  setTheme: () => {},
});

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeId>(loadStoredTheme);
  const [resolvedScheme, setResolvedScheme] = useState<"light" | "dark">(() => {
    const r = resolveTheme(loadStoredTheme());
    return r === "midnight" ? "dark" : "light";
  });

  // Keep resolvedScheme in sync when theme is "system" and OS pref changes
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const update = () => {
      const r = resolveTheme(theme);
      setResolvedScheme(r === "midnight" ? "dark" : "light");
      if (theme === "system") {
        applyTheme("system"); // re-apply so data-theme updates
      }
    };
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, [theme]);

  const setTheme = (id: ThemeId) => {
    applyTheme(id);
    setThemeState(id);
    const r = resolveTheme(id);
    setResolvedScheme(r === "midnight" ? "dark" : "light");
  };

  return (
    <ThemeContext.Provider value={{ theme, resolvedScheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
